import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { formatDateParam, parseDateParam } from "helpers";

// Drop-in zamjena za `useState` za filter-objekte: konfigurirana polja se sinkroniziraju s URL query
// paramovima (preživljavaju reload + share preko URL-a), a ostala polja se ponašaju kao običan state.
//
//   const [filters, setFilters] = useUrlFilters(initialForm, {
//     search:          { param: "q" },
//     condition:       { param: "condition" },
//     expenseDateFrom: { param: "from", type: "date" },
//     expenseDateTo:   { param: "to", type: "date-end" },
//   });
//
// - Init: čita svaki konfigurirani param iz URL-a i (ako postoji) override-a to polje u `initial`.
// - setFilters(next | prev => next): postavi state + upiši konfigurirana polja u URL (prazno se briše),
//   uz `replace` (bez zatrpavanja povijesti); ostali (nekonfiguirani) paramovi u URL-u ostaju netaknuti.
// - popstate: back/forward u pregledniku re-sinkronizira konfigurirana polja iz URL-a u state.

export type UrlFilterFieldType = "string" | "number" | "boolean" | "date" | "date-end" | "list";

export interface UrlFilterFieldConfig {
  param: string;
  type?: UrlFilterFieldType; // default "string"
}

export type UrlFilterConfig<T> = Partial<Record<keyof T, UrlFilterFieldConfig>>;

const isEmpty = (v: any): boolean =>
  v == null ||
  v === "" ||
  (typeof v === "number" && isNaN(v)) ||
  (Array.isArray(v) && v.length === 0);

// Dekodira sirovi string iz URL-a u vrijednost polja prema tipu.
const decode = (raw: string, type: UrlFilterFieldType | undefined): any => {
  switch (type) {
    case "number": {
      const n = Number(raw);
      return isNaN(n) ? null : n;
    }
    case "boolean":
      return raw === "true";
    case "date":
      return parseDateParam(raw, false);
    case "date-end":
      return parseDateParam(raw, true);
    // Lista vrijednosti u URL-u je zarezom odvojena (npr. ?status=AVAILABLE,RESERVED).
    case "list":
      return raw.split(",").filter(Boolean);
    default:
      return raw;
  }
};

// Enkodira vrijednost polja u string za URL (ili null ako je prazno).
const encode = (value: any, type: UrlFilterFieldType | undefined): string | null => {
  if (isEmpty(value)) return null;
  switch (type) {
    case "number":
      return String(value);
    // `false` je podrazumijevano stanje checkboxa — ne piše se u URL da ostane čist.
    case "boolean":
      return value ? "true" : null;
    case "date":
    case "date-end":
      return formatDateParam(value as Date);
    case "list":
      return (value as string[]).join(",");
    default:
      return String(value);
  }
};

const useUrlFilters = <T extends object>(
  initial: T,
  config: UrlFilterConfig<T>
): [T, (next: T | ((prev: T) => T)) => void] => {
  const [, setSearchParams] = useSearchParams();

  // Config je stabilan po ekranu — memoiziramo entries da izbjegnemo re-kreiranje.
  const entries = useMemo(
    () => Object.entries(config) as [keyof T, UrlFilterFieldConfig][],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // Čita konfigurirana polja iz URL-a i vraća override preko `initial`.
  const readFromUrl = useCallback(
    (base: T): T => {
      const params = new URLSearchParams(window.location.search);
      const next = { ...base };
      for (const [field, cfg] of entries) {
        const raw = params.get(cfg.param);
        if (raw != null) (next as any)[field] = decode(raw, cfg.type);
      }
      return next;
    },
    [entries]
  );

  const [filters, setFiltersState] = useState<T>(() => readFromUrl(initial));

  // Upisuje konfigurirana polja u URL (prazno -> briše param), čuva ostale paramove.
  const writeToUrl = useCallback(
    (values: T) => {
      const params = new URLSearchParams(window.location.search);
      let changed = false;
      for (const [field, cfg] of entries) {
        const encoded = encode((values as any)[field], cfg.type);
        const current = params.get(cfg.param);
        if (encoded == null) {
          if (current != null) { params.delete(cfg.param); changed = true; }
        } else if (current !== encoded) {
          params.set(cfg.param, encoded);
          changed = true;
        }
      }
      if (changed) setSearchParams(params, { replace: true });
    },
    [entries, setSearchParams]
  );

  const setFilters = useCallback((next: T | ((prev: T) => T)) => {
    setFiltersState((prev) => (typeof next === "function" ? (next as (p: T) => T)(prev) : next));
  }, []);

  // URL sync tek nakon što se state slegne (bez side-effecta u state updateru).
  useEffect(() => {
    writeToUrl(filters);
  }, [filters, writeToUrl]);

  // Sinkronizacija na back/forward: resetiraj konfigurirana polja na initial pa primijeni URL
  // (tako da back koji ukloni param vrati zadanu vrijednost).
  const initialRef = useRef(initial);
  useEffect(() => {
    const onPopState = () => {
      setFiltersState((prev) => {
        const base = { ...prev };
        for (const [field] of entries) (base as any)[field] = (initialRef.current as any)[field];
        return readFromUrl(base);
      });
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, [entries, readFromUrl]);

  return [filters, setFilters];
};

export default useUrlFilters;
